import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useProcessMonitoring } from "../../context/ProcessMonitoringContext";

function formatRelative(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function formatProcessNumber(number) {
  const digits = String(number || "").replace(/\D/g, "");
  if (digits.length !== 20) return number || "";
  return `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14, 16)}.${digits.slice(16)}`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const {
    unreadCount,
    notifications,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    refreshAll,
  } = useProcessMonitoring();

  const load = useCallback(async () => {
    try {
      await Promise.allSettled([
        refreshAll(),
        fetchNotifications({ includeRead: false }),
      ]);
    } catch {
      // polling silencioso
    }
  }, [refreshAll, fetchNotifications]);

  useEffect(() => {
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (open) {
      fetchNotifications({ includeRead: false });
    }
  }, [open, fetchNotifications]);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function handleClick(n) {
    if (!n.read) {
      try {
        await markNotificationRead(n.id);
      } catch {}
    }
    setOpen(false);
    navigate("/meus-monitoramentos", { state: { tab: "notifications" } });
  }

  async function markAll() {
    try {
      await markAllNotificationsRead();
      await fetchNotifications({ includeRead: false });
    } catch {}
  }

  const items = (notifications || []).slice(0, 20);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative mr-2 p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
        aria-label="Notificações de monitoramentos"
        title={
          unreadCount > 0
            ? `${unreadCount} notificação(ões) de processos monitorados`
            : "Nenhuma notificação de monitoramento"
        }
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] leading-[18px] text-center ring-2 ring-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-900">Processos monitorados</span>
            {unreadCount > 0 && (
              <button onClick={markAll} className="text-xs text-blue-600 hover:text-blue-700">
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                Sem notificações de monitoramento
              </div>
            ) : (
              items.map((n) => {
                const tribunal = n.publicationDje?.siglaTribunal || "Tribunal";
                const summary = n.publicationDje?.resumo || "Nova publicação no processo monitorado";

                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-gray-900">
                        {tribunal}
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {formatRelative(n.createdAt)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5 font-mono">
                      {formatProcessNumber(n.numberProcess)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1 line-clamp-2">{summary}</div>
                  </button>
                );
              })
            )}
          </div>
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/meus-monitoramentos", { state: { tab: "notifications" } });
              }}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Ver todas em Meus monitoramentos
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
